import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Modal,
  SimpleGrid,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconArrowLeft,
  IconEdit,
  IconEye,
  IconUsersGroup,
} from "@tabler/icons-react";
import { useClient } from "../../../context/ClientContext";
import usePermissions from "../../../hooks/usePermissions";
import ClientForm from "../ClientForm";
import {
  Client,
  DuplicateReason,
  PotentialDuplicateGroup,
} from "../../../types/client";
import { formatPhoneNumber } from "../../../utils/phoneUtils";
import styles from "./ClientDuplicatesPage.module.css";

const REASON_WORD: Record<DuplicateReason, string> = {
  email: "EMAIL",
  phone: "PHONE",
  name: "NAME",
};

// Badge color by number of criteria matched: 1 -> yellow, 2 -> orange, 3 -> red.
const SEVERITY_COLOR: Record<number, string> = {
  1: "yellow",
  2: "orange",
  3: "red",
};

/** "SAME EMAIL" / "SAME EMAIL & PHONE" / "SAME EMAIL, PHONE & NAME". */
const duplicateLabel = (reasons: DuplicateReason[]): string => {
  const words = (reasons ?? []).map((r) => REASON_WORD[r]).filter(Boolean);
  if (words.length === 0) return "POTENTIAL DUPLICATE";
  if (words.length === 1) return `SAME ${words[0]}`;
  const last = words[words.length - 1];
  return `SAME ${words.slice(0, -1).join(", ")} & ${last}`;
};

/**
 * Reasons for a group, tolerant of the legacy single-`reason` payload (so the
 * page doesn't crash if it loads before the new get_clients shape is deployed).
 */
const groupReasons = (group: PotentialDuplicateGroup): DuplicateReason[] => {
  if (group.reasons?.length) return group.reasons;
  const legacy = (group as { reason?: DuplicateReason }).reason;
  return legacy ? [legacy] : [];
};

const phone = (c: Client): string =>
  c.area_code || c.phone_number
    ? formatPhoneNumber(`${c.area_code ?? ""}${c.phone_number ?? ""}`)
    : "No phone";

/**
 * "Potential Duplicates" review screen. Lists each flagged group with the
 * reason it matched, per-record view/edit shortcuts to the client profile, and
 * a one-click "Review & Merge" into the merge wizard.
 */
const ClientDuplicatesPage: React.FC = () => {
  const navigate = useNavigate();
  const perms = usePermissions();
  const canEditClients = perms?.clients?.canView !== false;
  const { allClients, potentialDuplicates, refreshClients } = useClient();
  const groups: PotentialDuplicateGroup[] = potentialDuplicates ?? [];

  const [editClient, setEditClient] = useState<Client | null>(null);

  const resolve = (ids: string[]): Client[] =>
    ids
      .map((id) => allClients.find((c: Client) => c.id === id))
      .filter((c): c is Client => Boolean(c));

  const reviewMerge = (group: PotentialDuplicateGroup) => {
    navigate("/admin/clients/merge", {
      state: { clientIds: group.client_ids },
    });
  };

  const closeEdit = () => {
    setEditClient(null);
    // Re-evaluate duplicates in case the edit changed a matching field.
    refreshClients?.();
  };

  return (
    <Box p={{ base: "sm", sm: "lg" }} maw={1100} mx="auto">
      <Button
        variant="subtle"
        color="gray"
        leftSection={<IconArrowLeft size={16} />}
        onClick={() => navigate("/admin/clients")}
        mb="md"
      >
        Back to Clients
      </Button>

      <Group gap="md" mb="lg" align="flex-start">
        <Box className={styles.iconWrap}>
          <IconUsersGroup size={22} />
        </Box>
        <Stack gap={2}>
          <Title order={2}>Potential Duplicates</Title>
          <Text c="dimmed">
            Records that share a name, phone number, or email address.
          </Text>
        </Stack>
      </Group>

      {groups.length === 0 ? (
        <Card withBorder radius="md" p="xl">
          <Text c="dimmed" ta="center">
            No potential duplicates found. Your client list looks clean!
          </Text>
        </Card>
      ) : (
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          {groups.map((group, idx) => {
            const members = resolve(group.client_ids);
            if (members.length < 2) return null;
            const reasons = groupReasons(group);
            const matchCount = group.match_count ?? reasons.length;
            return (
              <Card key={idx} withBorder radius="md" p="lg">
                <Group justify="space-between" mb="md">
                  <Badge
                    color={SEVERITY_COLOR[matchCount] ?? "yellow"}
                    variant="light"
                    radius="sm"
                    size="lg"
                    leftSection={<IconAlertCircle size={14} />}
                  >
                    {duplicateLabel(reasons)}
                  </Badge>
                  <Button
                    color="var(--brand-teal)"
                    onClick={() => reviewMerge(group)}
                  >
                    Review &amp; Merge
                  </Button>
                </Group>
                <Stack gap="sm">
                  {members.map((member) => (
                    <Box key={member.id} className={styles.memberRow}>
                      <Group justify="space-between" wrap="nowrap">
                        <Box style={{ minWidth: 0 }}>
                          <Text fw={600} truncate>
                            {member.first_name} {member.last_name}
                          </Text>
                          <Group gap="lg">
                            <Text size="sm" c="dimmed" truncate>
                              {member.email || "No email"}
                            </Text>
                            <Text size="sm" c="dimmed">
                              {phone(member)}
                            </Text>
                          </Group>
                        </Box>
                        <Group gap="xs" wrap="nowrap">
                          <Tooltip label="View profile">
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              onClick={() =>
                                navigate(`/admin/clients/${member.id}`)
                              }
                              aria-label={`View ${member.first_name} ${member.last_name}`}
                            >
                              <IconEye size={16} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label={canEditClients ? "Edit" : "View"}>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              onClick={() => setEditClient(member)}
                              aria-label={`Edit ${member.first_name} ${member.last_name}`}
                            >
                              <IconEdit size={16} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Group>
                    </Box>
                  ))}
                </Stack>
              </Card>
            );
          })}
        </SimpleGrid>
      )}

      <Modal
        opened={!!editClient}
        onClose={closeEdit}
        title="Edit Client"
        size="xl"
      >
        <ClientForm
          client={editClient}
          onClose={closeEdit}
          readOnly={!canEditClients}
        />
      </Modal>
    </Box>
  );
};

export default ClientDuplicatesPage;
